import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';

const PLAN_NAMES = {
  gratuit: 'Plan Gratuit', pro: 'Plan Pro', promax: 'Plan Pro Max', 'boost-ia': "Boostez avec l'IA"
};

const RETRY_DELAYS_MS = [3000, 5000, 8000, 12000, 20000];

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [planName, setPlanName] = useState('');

  useEffect(() => {
    const transactionId = searchParams.get('id');
    if (!transactionId) {
      setStatus('failed');
      return undefined;
    }
    let cancelled = false;
    let timer;
    // Mobile money : la confirmation peut prendre quelques instants. La
    // vérification serveur est idempotente : revérifier n'active jamais deux fois.
    const verify = async (attempt) => {
      try {
        const res = await axios.get(`/api/payments/verify/${encodeURIComponent(transactionId)}`);
        if (cancelled) return;
        if (res.data.status === 'approved') {
          setPlanName(PLAN_NAMES[res.data.planId] || res.data.planId);
          setStatus('success');
          toast.success('Paiement effectué avec succès !');
        } else if (['declined', 'canceled', 'cancelled', 'refunded'].includes(res.data.status)) {
          setStatus('failed');
        } else if (attempt < RETRY_DELAYS_MS.length) {
          timer = setTimeout(() => verify(attempt + 1), RETRY_DELAYS_MS[attempt]);
        } else {
          setStatus('pending');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    };
    verify(0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="card p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mx-auto mb-6"></div>
              <h2 className="text-xl font-semibold text-text mb-2">Vérification du paiement…</h2>
              <p className="text-gray-500 text-sm">Veuillez patienter, nous confirmons votre transaction.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircleIcon className="h-20 w-20 text-green-500 mx-auto mb-5" />
              <h2 className="text-2xl font-bold text-text mb-2">Paiement réussi !</h2>
              <p className="text-gray-600 mb-6">
                Votre abonnement <strong>{planName}</strong> est maintenant actif.
                Profitez de toutes les fonctionnalités incluses.
              </p>
              <button
                onClick={() => navigate('/payment')}
                className="w-full py-2 px-4 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium mb-3"
              >
                Voir ma facturation
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Aller au tableau de bord
              </button>
            </>
          )}

          {(status === 'pending' || status === 'error') && (
            <>
              <h2 className="text-2xl font-bold text-text mb-2">
                {status === 'pending' ? 'Paiement en cours de confirmation' : 'Vérification impossible pour le moment'}
              </h2>
              <p className="text-gray-600 mb-6">
                {status === 'pending'
                  ? "L'opérateur n'a pas encore confirmé votre paiement. Votre abonnement sera activé automatiquement dès la confirmation : inutile de payer une seconde fois."
                  : "Nous n'avons pas pu joindre le service de paiement. Si vous avez payé, votre abonnement sera activé automatiquement : ne payez pas une seconde fois."}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="w-full py-2 px-4 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium mb-3"
              >
                Vérifier à nouveau
              </button>
              <button
                onClick={() => navigate('/payment')}
                className="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Voir ma facturation
              </button>
            </>
          )}

          {status === 'failed' && (
            <>
              <XCircleIcon className="h-20 w-20 text-red-400 mx-auto mb-5" />
              <h2 className="text-2xl font-bold text-text mb-2">Paiement non complété</h2>
              <p className="text-gray-600 mb-6">
                Le paiement n'a pas pu être finalisé. Aucun montant n'a été débité.
                Vous pouvez réessayer à tout moment.
              </p>
              <button
                onClick={() => navigate('/subscriptions')}
                className="w-full py-2 px-4 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium mb-3"
              >
                Réessayer
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Retour au tableau de bord
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCallback;
